import './App.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Seo from './components/Seo';
import stripePromise from './stripePromise';
import { useSubscription } from './useSubscription';
import Paywall from './Paywall';
import CheckoutModal from './components/CheckoutModal';
import FrequencyTab from './components/FrequencyTab';
import HotColdTab from './components/HotColdTab';
import GapAnalysisTab from './components/GapAnalysisTab';
import NumberPickerTab from './components/NumberPickerTab';
import BacktestTab from './components/BacktestTab';
import DrawHistoryTab from './components/DrawHistoryTab';
import HeatmapTab from './components/HeatmapTab';
import TrendsTab from './components/TrendsTab';
import DistributionTab from './components/DistributionTab';
import PairsTab from './components/PairsTab';
import ErrorBoundary from './components/ErrorBoundary';
import SocialLinks from './components/SocialLinks';
import AuthModal from './components/AuthModal';
import WelcomeModal from './components/WelcomeModal';
import { useAuth } from './AuthContext';


const PLAN_META = {
  'price_1TKOZvILOpB6RcrOkie5R5Kb': { label: 'Yearly plan',  price: '$24.99 / year'  },
  'price_1TKORnILOpB6RcrOkFwJKC0e': { label: 'Monthly plan', price: '$3.99 / month' },
};

// ---------------------------------------------------------------------------
// Game configurations
// ---------------------------------------------------------------------------
const GAMES = {
  powerball: {
    id: 'powerball',
    name: 'Powerball',
    apiId: 'd6yy-54nr',
    mainCount: 5,
    maxMain: 69,
    bonusName: 'Powerball',
    maxBonus: 26,
    accentColor: '#E32636',
    bonusColor: '#E32636',
    parseRow: (row) => {
      const nums = row.winning_numbers.trim().split(' ').map(Number);
      return {
        date: row.draw_date.substring(0, 10),
        main: nums.slice(0, 5),
        bonus: nums[5] || null,
        multiplier: row.multiplier || null,
      };
    },
  },
  megamillions: {
    id: 'megamillions',
    name: 'Mega Millions',
    apiId: '5xaw-6ayf',
    mainCount: 5,
    maxMain: 70,
    bonusName: 'Mega Ball',
    maxBonus: 25,
    accentColor: '#003DA5',
    bonusColor: '#FFC72C',
    parseRow: (row) => {
      const nums = row.winning_numbers.trim().split(' ').map(Number);
      return {
        date: row.draw_date.substring(0, 10),
        main: nums.slice(0, 5),
        bonus: row.mega_ball ? parseInt(row.mega_ball, 10) : null,
        multiplier: null,
      };
    },
  },
};

// ---------------------------------------------------------------------------
// Date range options
// ---------------------------------------------------------------------------
const DATE_RANGES = [
  { label: 'All time', value: 'all' },
  { label: 'Last 5 yrs', value: '5y' },
  { label: 'Last 3 yrs', value: '3y' },
  { label: 'Last year', value: '1y' },
];

function getDateLimit(range) {
  const now = new Date();
  if (range === '1y') return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString().substring(0, 10);
  if (range === '3y') return new Date(now.getFullYear() - 3, now.getMonth(), now.getDate()).toISOString().substring(0, 10);
  if (range === '5y') return new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()).toISOString().substring(0, 10);
  return null;
}

// ---------------------------------------------------------------------------
// Cryptographically secure random number in [0, 1)
// Using crypto.getRandomValues for statistical integrity
// ---------------------------------------------------------------------------
function cryptoRandom() {
  return crypto.getRandomValues(new Uint32Array(1))[0] / (2 ** 32);
}

// ---------------------------------------------------------------------------
// Statistics helpers
// ---------------------------------------------------------------------------
function computeStats(draws, maxNum, ballType) {
  const freq = {};
  const lastSeen = {};
  for (let i = 1; i <= maxNum; i++) { freq[i] = 0; lastSeen[i] = null; }

  const sorted = [...draws].sort((a, b) => b.date.localeCompare(a.date));

  sorted.forEach((draw, idx) => {
    const balls = ballType === 'main' ? draw.main : (draw.bonus ? [draw.bonus] : []);
    balls.forEach((n) => {
      if (n >= 1 && n <= maxNum) {
        freq[n] = (freq[n] || 0) + 1;
        if (lastSeen[n] === null) lastSeen[n] = idx;
      }
    });
  });

  for (let i = 1; i <= maxNum; i++) {
    if (lastSeen[i] === null) lastSeen[i] = sorted.length;
  }

  return { freq, lastSeen };
}

// ---------------------------------------------------------------------------
// Tab configuration
// ---------------------------------------------------------------------------
const CATEGORIES = [
  { key: 'stats',     label: 'Stats'     },
  { key: 'analytics', label: 'Analytics', pro: true },
  { key: 'tools',     label: 'Tools'     },
];

const TABS = [
  { key: 'freq',      label: 'Frequency',    category: 'stats'     },
  { key: 'balls',     label: 'Freq. Rank',   category: 'stats'     },
  { key: 'history',   label: 'History',      category: 'stats'     },
  { key: 'heatmap',   label: 'Heatmap',      category: 'stats'     },
  { key: 'trends',    label: 'Trends',       category: 'analytics' },
  { key: 'dist',      label: 'Distribution', category: 'analytics' },
  { key: 'pairs',     label: 'Pairs',        category: 'analytics' },
  { key: 'gaps',      label: 'Gaps',         category: 'analytics' },
  { key: 'pick',      label: 'Picker',       category: 'tools'     },
  { key: 'backtest',  label: 'Backtest',     category: 'tools'     },
];

const PRO_TABS = ['trends', 'dist', 'pairs', 'gaps', 'backtest'];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function App() {
  const [gameId, setGameId] = useState('powerball');
  const [category, setCategory] = useState('stats');
  const [tab, setTab] = useState('freq');
  const [dateRange, setDateRange] = useState('all');
  const [pickMode, setPickMode] = useState('random');
  const [picks, setPicks] = useState({ main: [], bonus: null });
  const [allDraws, setAllDraws] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ballView, setBallView] = useState('main');
  const [animKey, setAnimKey] = useState(0);
  const [showPaywall,    setShowPaywall]    = useState(false);
  const [showAuthModal,  setShowAuthModal]  = useState(null); // null | 'signin' | 'signup'
  const [showWelcome,    setShowWelcome]    = useState(false);
  const [checkout,       setCheckout]       = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const { session } = useAuth();
  const { isPro, refetch: refetchSub } = useSubscription();

  const game = GAMES[gameId];

  // Show welcome modal once per session when user is not logged in
  useEffect(() => {
    if (session === null) setShowWelcome(true);
    if (session)         setShowWelcome(false);
  }, [session]);

  // Open auth modal if not logged in, otherwise open paywall
  const openUpgradeFlow = useCallback(() => {
    if (!session) {
      setShowAuthModal('signin');
    } else {
      setShowPaywall(true);
    }
  }, [session]);

  // Called when user picks a plan in the Paywall — creates subscription on server
  const handleSelectPlan = useCallback(async (priceId, promoResult = null) => {
    if (!session) return;
    setShowPaywall(false);
    setCheckoutLoading(true);
    try {
      const res  = await fetch('/api/create-subscription.php', {
        method:  'POST',
        headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ price_id: priceId }),
      });
      const data = await res.json();
      if (data.clientSecret) {
        setCheckout({
          clientSecret:    data.clientSecret,
          priceId,
          promotionCodeId: promoResult?.promotionCodeId ?? null,
          promoDiscount:   promoResult ? { name: promoResult.name, percentOff: promoResult.percentOff, amountOff: promoResult.amountOff } : null,
        });
      } else {
        console.error('[checkout] Server error:', data.error);
      }
    } catch (err) {
      console.error('[checkout] Network error:', err);
    } finally {
      setCheckoutLoading(false);
    }
  }, [session]);

  const handleCheckoutSuccess = useCallback(() => {
    setCheckout(null);
    refetchSub();
  }, [refetchSub]);

  const handleCategoryClick = useCallback((cat) => {
    setCategory(cat.key);
    const catTabs = TABS.filter(t => t.category === cat.key);
    const firstFree = catTabs.find(t => !PRO_TABS.includes(t.key) || isPro);
    if (firstFree) setTab(firstFree.key);
    // If all tabs in category are pro-locked, show locked tabs (click to trigger paywall)
  }, [isPro]);

  const handleTabClick = useCallback((t) => {
    if (PRO_TABS.includes(t) && !isPro) {
      openUpgradeFlow();
    } else {
      setTab(t);
      const tabDef = TABS.find(td => td.key === t);
      if (tabDef) setCategory(tabDef.category);
    }
  }, [isPro, openUpgradeFlow]);

  // Filter draws by selected date range
  const draws = useMemo(() => {
    const limit = getDateLimit(dateRange);
    if (!limit) return allDraws;
    return allDraws.filter((d) => d.date >= limit);
  }, [allDraws, dateRange]);

  // Compute main ball stats
  const { freq, lastSeen } = useMemo(
    () => (draws.length ? computeStats(draws, game.maxMain, 'main') : { freq: {}, lastSeen: {} }),
    [draws, game.maxMain]
  );

  // Compute bonus ball stats
  const { freq: bonusFreq } = useMemo(
    () => (draws.length ? computeStats(draws, game.maxBonus, 'bonus') : { freq: {}, lastSeen: {} }),
    [draws, game.maxBonus]
  );

  // Entries sorted various ways
  const entries = useMemo(
    () => Object.entries(freq).map(([n, f]) => ({ n: +n, f })),
    [freq]
  );
  const sortedByFreq = useMemo(() => [...entries].sort((a, b) => b.f - a.f), [entries]);
  const sortedByNum = useMemo(() => [...entries].sort((a, b) => a.n - b.n), [entries]);

  const hotThresh = useMemo(
    () => sortedByFreq[Math.floor(game.maxMain * 0.2)]?.f ?? 0,
    [sortedByFreq, game.maxMain]
  );
  const coldThresh = useMemo(
    () => sortedByFreq[Math.floor(game.maxMain * 0.8)]?.f ?? 0,
    [sortedByFreq, game.maxMain]
  );
  const warmThresh = useMemo(
    () => sortedByFreq[Math.floor(game.maxMain * 0.4)]?.f ?? 0,
    [sortedByFreq, game.maxMain]
  );
  const avgFreq = useMemo(
    () => (entries.length ? Math.round(entries.reduce((a, e) => a + e.f, 0) / entries.length) : 0),
    [entries]
  );

  const getBallClass = useCallback(
    (f) => {
      if (f >= hotThresh) return 'hot';
      if (f >= warmThresh) return 'warm';
      if (f > coldThresh) return 'neutral';
      return 'cold';
    },
    [hotThresh, warmThresh, coldThresh]
  );

  const getBarColor = useCallback(
    (f) => {
      if (f >= hotThresh) return '#D85A30';
      if (f >= warmThresh) return '#EF9F27';
      if (f > coldThresh) return '#888780';
      return '#378ADD';
    },
    [hotThresh, warmThresh, coldThresh]
  );

  // Fetch data when game changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    setAllDraws([]);
    setPicks({ main: [], bonus: null });

    const url = `https://data.ny.gov/resource/${game.apiId}.json?$order=draw_date%20ASC&$limit=5000`;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const parsed = data
          .map(game.parseRow)
          .filter((d) => d.main.length === game.mainCount);
        setAllDraws(parsed);
        setLoading(false);
      })
      .catch(() => {
        setError('Could not load draw data. Check your connection and try again.');
        setLoading(false);
      });
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Number picker
  const generatePick = useCallback(() => {
    if (!entries.length) return;

    let pool = [];
    if (pickMode === 'weighted') {
      entries.forEach(({ n, f }) => {
        for (let i = 0; i < Math.max(f, 1); i++) pool.push(n);
      });
    } else if (pickMode === 'overdue') {
      pool = Object.entries(lastSeen)
        .sort((a, b) => b[1] - a[1])
        .slice(0, Math.ceil(game.maxMain * 0.4))
        .map(([n]) => +n);
    } else {
      pool = entries.map((e) => e.n);
    }

    const picked = [];
    const copy = [...pool];
    while (picked.length < game.mainCount && copy.length > 0) {
      const idx = Math.floor(cryptoRandom() * copy.length);
      const num = copy[idx];
      if (!picked.includes(num)) picked.push(num);
      copy.splice(idx, 1);
    }

    const bonus = Math.floor(cryptoRandom() * game.maxBonus) + 1;
    setPicks({ main: picked.sort((a, b) => a - b), bonus });
    setAnimKey((k) => k + 1);
  }, [pickMode, entries, lastSeen, game]);

  useEffect(() => { generatePick(); }, [pickMode, entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gap table data
  const gapEntries = useMemo(
    () =>
      Object.entries(lastSeen)
        .map(([n, g]) => ({ n: +n, g }))
        .sort((a, b) => b.g - a.g)
        .slice(0, 25),
    [lastSeen]
  );
  const maxGap = gapEntries[0]?.g || 1;

  // Bonus ball sorted by frequency
  const bonusEntries = useMemo(
    () => Object.entries(bonusFreq).map(([n, f]) => ({ n: +n, f })).sort((a, b) => b.f - a.f),
    [bonusFreq]
  );

  const firstDate = draws[0]?.date || '';
  const lastDate = draws[draws.length - 1]?.date || '';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="app">
      <Seo
        path="/app"
        title="LottoPickle App — Lottery Frequency, Heatmaps & Smart Picker"
        description="Explore Powerball and Mega Millions draw data with frequency charts, hot & cold trackers, heatmaps, trends, pair and gap analysis, and a smart number picker."
      />

      {/* Header */}
      <div className="header">
        <div className="header-left">
          <Link to="/" className="header-logo-link"><h1>LottoPickle</h1></Link>
          <span className="tagline">Lottery draw history, visualized</span>
          {draws.length > 0 && (
            <span className="data-range">
              {new Intl.DateTimeFormat('en-US').format(new Date(firstDate))} to {new Intl.DateTimeFormat('en-US').format(new Date(lastDate))}
            </span>
          )}
        </div>
        <div className="header-right">
          <div className="header-nav-row">
            {session ? (
              <Link to="/account" className="home-link account-link">Account</Link>
            ) : (
              <button className="home-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setShowAuthModal(true)}>Sign in</button>
            )}
          </div>
          <div className="game-selector">
            {Object.values(GAMES).map((g) => (
              <button
                key={g.id}
                className={`game-btn ${g.id} ${gameId === g.id ? 'active' : ''}`}
                onClick={() => { setGameId(g.id); setTab('freq'); setBallView('main'); }}
              >
                {g.name}
              </button>
            ))}
            <Link to="/pick3" className="game-btn pick3">NC Pick 3</Link>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading {game.name} data...</span>
        </div>
      )}

      {/* Error */}
      {error && <div className="error-banner">{error}</div>}

      {/* Main content */}
      {!loading && !error && draws.length > 0 && (
        <>
          {/* Date range filter */}
          <div className="date-range-row">
            {DATE_RANGES.map((r) => (
              <button
                key={r.value}
                className={`range-btn ${dateRange === r.value ? 'active' : ''}`}
                onClick={() => setDateRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* Metrics */}
          <div className="metric-row">
            {[
              { label: 'Draws in dataset', val: draws.length, cls: 'neutral' },
              { label: 'Most frequent', val: sortedByFreq[0]?.n, cls: 'hot' },
              { label: 'Least frequent', val: sortedByFreq[sortedByFreq.length - 1]?.n, cls: 'cold' },
              { label: 'Avg draw count', val: avgFreq, cls: 'neutral' },
            ].map((m) => (
              <div className="metric" key={m.label}>
                <div className="metric-label">{m.label}</div>
                <div className={`metric-val ${m.cls}`}>{m.val}</div>
              </div>
            ))}
          </div>

          {/* Category nav */}
          <div className="tab-categories">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                className={`tab-cat-btn ${category === cat.key ? 'active' : ''}`}
                onClick={() => handleCategoryClick(cat)}
              >
                {cat.label}
                {cat.pro && !isPro && <span className="tab-cat-star">⭐</span>}
              </button>
            ))}
          </div>

          {/* Sub-tabs for current category */}
          <div className="tabs">
            {TABS.filter(t => t.category === category).map(({ key, label }) => {
              const locked = PRO_TABS.includes(key) && !isPro;
              return (
                <button
                  key={key}
                  className={`tab ${tab === key ? 'active' : ''} ${locked ? 'tab-locked' : ''}`}
                  onClick={() => handleTabClick(key)}
                >
                  {label}
                  {locked && <span className="tab-lock">🔒</span>}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <ErrorBoundary key={tab}>
            {tab === 'freq' && (
              <FrequencyTab game={game} sortedByNum={sortedByNum} getBarColor={getBarColor} bonusFreq={bonusFreq} />
            )}
            {tab === 'balls' && (
              <HotColdTab game={game} sortedByFreq={sortedByFreq} bonusEntries={bonusEntries} getBallClass={getBallClass} ballView={ballView} setBallView={setBallView} />
            )}
            {tab === 'history' && (
              <DrawHistoryTab game={game} draws={draws} freq={freq} getBallClass={getBallClass} />
            )}
            {tab === 'heatmap' && (
              <HeatmapTab game={game} freq={freq} bonusFreq={bonusFreq} />
            )}
            {tab === 'trends' && (
              <TrendsTab game={game} draws={draws} sortedByFreq={sortedByFreq} />
            )}
            {tab === 'dist' && (
              <DistributionTab entries={entries} avgFreq={avgFreq} />
            )}
            {tab === 'pairs' && (
              <PairsTab draws={draws} />
            )}
            {tab === 'gaps' && (
              <GapAnalysisTab gapEntries={gapEntries} maxGap={maxGap} />
            )}
            {tab === 'pick' && (
              <NumberPickerTab game={game} pickMode={pickMode} setPickMode={setPickMode} picks={picks} generatePick={generatePick} animKey={animKey} isPro={isPro} onShowPaywall={openUpgradeFlow} />
            )}
            {tab === 'backtest' && (
              <BacktestTab game={game} draws={draws} freq={freq} lastSeen={lastSeen} getBallClass={getBallClass} />
            )}
          </ErrorBoundary>

          <p className="disclaimer">
            For entertainment and data analysis purposes only. Lottery draws are random - historical
            frequency has no bearing on future results. Not gambling advice.
          </p>
        </>
      )}

      {/* Welcome modal — shown to logged-out users on first visit */}
      {showWelcome && !showAuthModal && !showPaywall && (
        <WelcomeModal
          onSignUp={() => { setShowWelcome(false); setShowAuthModal('signup'); }}
          onSignIn={() => { setShowWelcome(false); setShowAuthModal('signin'); }}
          onDismiss={() => setShowWelcome(false)}
        />
      )}

      {/* Paywall */}
      {showPaywall && (
        <Paywall
          onSelectPlan={handleSelectPlan}
          onClose={() => setShowPaywall(false)}
        />
      )}

      {/* Auth modal — shown before paywall if not logged in */}
      {showAuthModal && (
        <AuthModal
          initialMode={showAuthModal}
          reason="Create a free account to subscribe"
          onSuccess={async (newSession) => {
            setShowAuthModal(null);
            if (newSession?.access_token) {
              const res  = await fetch('/api/subscription-status.php', {
                headers: { Authorization: 'Bearer ' + newSession.access_token },
              });
              const data = await res.json();
              if (!data.isPro) setShowPaywall(true);
              refetchSub();
            }
          }}
          onClose={() => setShowAuthModal(null)}
        />
      )}

      {/* Checkout modal — Stripe Elements card form */}
      {checkout && (
        <CheckoutModal
          stripePromise={stripePromise}
          clientSecret={checkout.clientSecret}
          priceId={checkout.priceId}
          accessToken={session?.access_token}
          promotionCodeId={checkout.promotionCodeId}
          promoDiscount={checkout.promoDiscount}
          planLabel={PLAN_META[checkout.priceId]?.label ?? 'Premium'}
          planPrice={PLAN_META[checkout.priceId]?.price ?? ''}
          onSuccess={handleCheckoutSuccess}
          onClose={() => setCheckout(null)}
        />
      )}

      {/* Checkout loading overlay */}
      {checkoutLoading && (
        <div className="checkout-loading-overlay">
          <div className="spinner" />
          <span>Setting up your subscription…</span>
        </div>
      )}

      {/* Footer */}
      <footer className="app-footer">
        <div className="app-footer-top">
          <SocialLinks variant="footer" />
        </div>
        <div className="app-footer-links">
          <Link to="/about">About</Link>
          <Link to="/pick3">NC Pick 3</Link>
          <Link to="/data-science">Data Science</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/disclaimer">Disclaimer</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/refunds">Refunds</Link>
          <Link to="/eula">EULA</Link>
          <Link to="/support">Support</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <p className="app-footer-gambling">
          Must be 18+. If you or someone you know has a gambling problem, call the NCPG Helpline: <a href="tel:18005224700">1-800-522-4700</a>
        </p>
      </footer>

    </div>
  );
}
